import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { createSafeCloseHandler } from '../../utils/modalUtils';

interface AddDailyUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddItem: (itemData: any) => Promise<void>;
  projectData: ProjectInterface;
}

interface DailyUpdateFormData {
  location: string;
  what_done: string;
  what_goal: string;
  what_blocks: string;
  resources_onsite: number;
  date: string;
}

export const AddDailyUpdateModal: React.FC<AddDailyUpdateModalProps> = ({
  isOpen,
  onClose,
  onAddItem,
  projectData,
}) => {
  const [formData, setFormData] = useState<DailyUpdateFormData>({
    location: '',
    what_done: '',
    what_goal: '',
    what_blocks: '',
    resources_onsite: 0,
    date: new Date().toISOString().split('T')[0]
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'resources_onsite' ? Number(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.date) {
      toast.error('Date is required');
      return;
    }

    setIsLoading(true);
    try {
      const itemData = {
        ...formData,
        project_id: projectData.id,
        date: formData.date ? new Date(formData.date).toISOString() : new Date().toISOString(),
      };
      
      console.log('AddDailyUpdateModal: submitting item data', itemData);
      
      await onAddItem(itemData);
      
      console.log('AddDailyUpdateModal: daily update added successfully');
      toast.success('Daily update added successfully');
      
      // Reset form
      setFormData({
        location: '',
        what_done: '',
        what_goal: '',
        what_blocks: '',
        resources_onsite: 0,
        date: new Date().toISOString().split('T')[0]
      });
      
      onClose();
    } catch (error) {
      console.error('AddDailyUpdateModal: failed to add daily update', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add daily update';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Create safe close handler
  const handleSafeClose = createSafeCloseHandler(
    formData, 
    onClose, 
    ['date', 'resources_onsite'] // Exclude auto-filled fields
  );

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50"
      onClick={handleSafeClose}
    >
      <button
        onClick={() => handleSafeClose()}
        className="fixed top-4 right-4 z-[60] text-gray-400 hover:text-gray-600 p-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Add Daily Update</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resources Onsite
              </label>
              <input
                type="number"
                name="resources_onsite"
                value={formData.resources_onsite}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                What is Done
              </label>
              <textarea
                name="what_done"
                value={formData.what_done}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Goal
              </label>
              <textarea
                name="what_goal"
                value={formData.what_goal}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Blocks
              </label>
              <textarea
                name="what_blocks"
                value={formData.what_blocks}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? 'Adding...' : 'Add Daily Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 